import { Head, Link } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MissionForm } from "@/components/missions/mission-form"
import AppLayout from "@/layouts/app-layout"
import { missionPath } from "@/routes"
import type { MissionsFormProps } from "@/types/missions"

/**
 * Brand/Missions/Edit — same shared `<MissionForm>` as New, pre-filled
 * with the persisted mission attributes (incl. signed_ids of attached
 * files for the dropzone's existing-attachments rendering).
 *
 * PATCH `missionPath(mission.id)`. The `commit` discriminator drives
 * status transitions (draft → active or activated mission edits).
 *
 * OKS-928 — passes `type_locked` through to `<MissionForm typeLocked>`.
 * The locked presentation (dimmed non-interactive cards + a `role="status"`
 * explanation) already existed and was tested; nothing rendered it because
 * no page ever supplied the prop, so the type selector invited a change the
 * model always rejected. `New` leaves it at its `false` default.
 */
export default function MissionsEdit({
  mission,
  brand_defaults,
  type_locked,
  catalog_products,
  can_detach_product,
}: MissionsFormProps) {
  const { t } = useTranslation()
  if (!mission.id) {
    throw new Error("MissionsEdit requires mission.id")
  }
  const id = mission.id

  return (
    <AppLayout>
      <Head title={t("missions.edit.head_title")} />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
        <header className="flex flex-col gap-3">
          <Link
            href={missionPath(id)}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="missions-edit-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("missions.head_title")}
          </Link>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("missions.edit.head_title")}
          </h1>
        </header>

        <MissionForm
          mission={mission}
          brandDefaults={brand_defaults}
          action={missionPath(id)}
          method="patch"
          typeLocked={type_locked}
          catalog={catalog_products}
          canDetachProduct={can_detach_product}
        />
      </div>
    </AppLayout>
  )
}
